import { Injectable } from '@angular/core';
import { App, AppState } from '@capacitor/app';
import { BehaviorSubject } from 'rxjs';
import {
  getEndpointForJobSource,
  JobSource,
} from 'src/app/_model/app-parameter';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { JobCreateRemoteActionBuilder } from 'src/app/_model/remote-action/remote-action-builder/job-create-remote-action-builder/job-create-remoteaction-builder';
import { UnassignServicejobRemoteActionBuilder } from 'src/app/_model/remote-action/remote-action-builder/unassign-servicejob-remote-action-builder/unassign-servicejob-remote-action-builder';
import {
  Servicejob,
  ServicejobCreateDto,
  ServicejobDTO,
  ServicejobStatus,
} from 'src/app/_model/servicejob';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { BillOfQuantitiesPositionsService } from 'src/app/_services/bill-of-quantities-positions/logic/bill-of-quantities-positions.service';
import { BillOfQuantitiesService } from 'src/app/_services/bill-of-quantities/logic/bill-of-quantities.service';
import { BranchesService } from 'src/app/_services/branches/logic/branches.service';
import { CargoItemsService } from 'src/app/_services/cargo-item/logic/cargo-item.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { DynFormsService } from 'src/app/_services/dyn-forms/logic/dyn-forms.service';
import { EmpJobMappingsService } from 'src/app/_services/emp-job-mapping/logic/emp-job-mapping.service';
import { EmployeeService } from 'src/app/_services/employee/logic/employee.service';
import { ExtFilesService } from 'src/app/_services/ext-files/logic/ext-files.service';
import { JobSectionsService } from 'src/app/_services/job-section/logic/job-section.service';
import { LocalStatusService } from 'src/app/_services/local-status/local-status.service';
import { LocationService } from 'src/app/_services/location/location.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { OrderItemsService } from 'src/app/_services/order-item/logic/order-item.service';
import { ReasonsService } from 'src/app/_services/reasons/logic/reasons.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { TagTemplatesElementsService } from 'src/app/_services/tag-templates-elements/logic/tag-templates-elements.service';
import { TagTemplatesService } from 'src/app/_services/tag-templates/logic/tag-templates.service';
import { TourStopsService } from 'src/app/_services/tour-stop/logic/tour-stop.service';
import { TourService } from 'src/app/_services/tour/logic/tour.service';
import { TimeUtils } from 'src/app/_util/time-utils';
import { ServicejobsDaoService } from '../../dao/servicejobs-dao.service';
import { ServicejobsService } from '../servicejobs.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { BadgesService } from 'src/app/_services/badges/logic/badges.service';
import { PreferencesService } from 'src/app/_services/preferences/preferences.service';
import { APILevel } from 'src/app/_services/sync/api-level';
import { ClientStateService } from 'src/app/_services/client-state/logic/client-state.service';
import { ClientState } from 'src/app/_model/client-state';

/**
 * Loads Servicejob
 */
@Injectable()
export class ServicejobsLogicService implements ServicejobsService {
  public servicejobs$ = new BehaviorSubject<Servicejob[]>([]);
  public plannedServicejobs$ = new BehaviorSubject<Servicejob[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Manually Inject this service to break the dependency circle
  // That is introduced by the loading of other data by the SerivceJob Service
  // like the dynForms
  public set dynFormsService(dynFormsService: DynFormsService) {
    this.dfService = dynFormsService;
  }
  public set empJobMappingService(empJobMappingService: EmpJobMappingsService) {
    this.ejmService = empJobMappingService;
    this.filterPlannedJobs();
  }
  public set billOfQuantitiesService(
    billOfQuantitiesService: BillOfQuantitiesService
  ) {
    this.boqService = billOfQuantitiesService;
  }
  public set billOfQuantitiesPositionsService(
    billOfQuantitiesPositionsService: BillOfQuantitiesPositionsService
  ) {
    this.boqpService = billOfQuantitiesPositionsService;
  }
  public set tourService(tourService: TourService) {
    this.tService = tourService;
  }
  public set tourStopsService(tourStopsService: TourStopsService) {
    this.tsService = tourStopsService;
  }
  public set cargoItemsService(cargoItemsService: CargoItemsService) {
    this.ciService = cargoItemsService;
  }
  public set orderItemsService(orderItemsService: OrderItemsService) {
    this.oiService = orderItemsService;
  }
  public set branchesService(branchesService: BranchesService) {
    this.bService = branchesService;
  }
  public set reasonsService(reasonsService: ReasonsService) {
    this.rService = reasonsService;
  }
  public set jobSectionsService(jobSectionsService: JobSectionsService) {
    this.jsService = jobSectionsService;
  }
  public set tagTemplatesService(tagTemplatesService: TagTemplatesService) {
    this.ttService = tagTemplatesService;
  }
  public set tagTemplatesElementsService(
    tagTemplatesElementsService: TagTemplatesElementsService
  ) {
    this.tteService = tagTemplatesElementsService;
  }
  public set clientStateService(
    clientStateService: ClientStateService
  ) {
    this.csService = clientStateService;
  }

  // Private Member
  private dfService?: DynFormsService;
  private efService?: ExtFilesService;
  private ejmService?: EmpJobMappingsService;
  private boqService?: BillOfQuantitiesService;
  private boqpService?: BillOfQuantitiesPositionsService;
  private tService?: TourService;
  private tsService?: TourStopsService;
  private ciService?: CargoItemsService;
  private oiService?: OrderItemsService;
  private bService?: BranchesService;
  private rService?: ReasonsService;
  private jsService?: JobSectionsService;
  private ttService?: TagTemplatesService;
  private tteService?: TagTemplatesElementsService;

  private csService?: ClientStateService;

  private temporaryLocalServicejobs?: Servicejob[];

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private servicejobDaoService: ServicejobsDaoService,
    private authenticationService: AuthenticationService,
    private appParameterService: AppParameterService,
    private appService: AppService,
    private credentialService: CredentialsService,
    private locationService: LocationService,
    private employeeService: EmployeeService,
    private localStatusService: LocalStatusService,
    private badgesService: BadgesService,
    private preferencesService: PreferencesService,
  ) {
    this.initialize();
    App.addListener('appStateChange', async (state: AppState) => {
      // We need to listen to the app state change
      // It might happen that the app is not restartet in the new day
      // So the planned servicejobs are still from the previous day
      if (state.isActive) {
        await this.loadLocalServicejobs();
      }
    });
  }

  // Public Function

  public async createServicejob(
    servicejobCreateDto: ServicejobCreateDto
  ): Promise<any> {
    let jcrab = new JobCreateRemoteActionBuilder(
      servicejobCreateDto,
      this.appService,
      this.credentialService,
      this.locationService,
      this.logger
    );

    const jobCreateRA = await jcrab.generateRemoteAction([]);

    await this.remoteActionService.sendRemoteAction(jobCreateRA);

    const branch =
      servicejobCreateDto.branchid != undefined
        ? this.bService.getBranchById({
            imsId: servicejobCreateDto.branchid,
            secondaryId: undefined,
            objRef: 'ims/Branch',
          })
        : undefined;
    const fullstreet =
      servicejobCreateDto.street == null &&
      servicejobCreateDto.streetnumber == null
        ? branch?.fullstreet
        : (servicejobCreateDto.street || '') +
          ' ' +
          (servicejobCreateDto.streetnumber || '');
    const pcdecity =
      servicejobCreateDto.zipcode == null && servicejobCreateDto.place == null
        ? branch?.pcdecity
        : (servicejobCreateDto.zipcode || '') +
          ' ' +
          (servicejobCreateDto.place || '');
    const localServiceJob: Servicejob = Servicejob.fromDto(
      {
        id: undefined,
        secondaryid: undefined,
        label:
          (servicejobCreateDto.accountingnumber
            ? servicejobCreateDto.accountingnumber + ' '
            : '') + (servicejobCreateDto.name ? servicejobCreateDto.name : ''),
        keywordlist: undefined,
        description: servicejobCreateDto.description,
        priority: undefined,
        status: ServicejobStatus.STATUS_CREATED,

        validpersonstring: branch?.defaultperson?.label,
        contactinfo: undefined,
        validphonenumber: branch?.validPhone,
        validmail: branch?.defaultpersonemail,

        validlatitude: branch?.validlatitude,
        validlongitude: branch?.validlongitude,
        validmatchingradius: branch?.validmatchingradius,

        employee: undefined,
        equipment: undefined,
        branch:
          branch != undefined
            ? {
                id: branch.id?.imsId,
                label: branch?.displayLabel,
              }
            : undefined,
        customerbranch: undefined,
        recipientbranch: undefined,
        location:
          branch?.location != undefined
            ? {
                id: branch.location?.id?.imsId,
                label: branch.location?.label,
              }
            : undefined,
        accountingnumber: servicejobCreateDto.accountingnumber,
        name: servicejobCreateDto.name,
        projsupervisor: undefined,
        supervisor: undefined,
        plannedtime: undefined,
        additionalinfotext: undefined,

        branchname: branch?.name,
        branchfullstreet: undefined,
        // branchfullstreet: branch?.city,
        branchcity: undefined,

        createdtstamp: Date.now(),
        geodataisaddresspreciseormanual: false,
        geodataismanual: false,

        pcdecity: pcdecity,
        fullstreet: fullstreet,
        appaccountingnumber: servicejobCreateDto.appaccountingnumber,

        validcustomername: servicejobCreateDto.customername,

        ispermanentjob: false,

        tourordernumber: undefined,
        tour: undefined,

        tourstop: undefined,

        tagtemplatevalid: undefined,
      },
      undefined,
      undefined,
      this.appParameterService.getFormatLabelForObject('ims/ServiceJob')
    );

    const savedSjob = await this.servicejobDaoService.createServiceJob(
      localServiceJob
    );

    if (
      this.appParameterService.appParameters$.getValue()?.[
        'imsmc.mode.jobsource'
      ] == JobSource.syncTeamPlannedJobsWithBranches ||
      this.appParameterService.appParameters$.getValue()?.[
        'imsmc.mode.jobsource'
      ] == JobSource.syncQuickPlannedJobsWithBranches
    ) {
      // If the job source is a planning we also need to create a empjobmapping for this day
      // otherwise the sjob will not be visible
      this.logger.log(
        this.constructor.name,
        'create an empjob mapping for today'
      );
      await this.ejmService.createEmpJobMappingForToday(savedSjob);
    }

    let sjobs = this.servicejobs$.getValue();
    sjobs.push(savedSjob);
    this.servicejobs$.next(sjobs);

    this.filterPlannedJobs();
  }

  public async addServicejob(servicejob: Servicejob) {
    let sjobs = this.servicejobs$.getValue();
    sjobs.push(servicejob);
    this.servicejobs$.next(sjobs);
    await this.servicejobDaoService.saveServicejobs(sjobs);
  }

  public getServiceJobById(id: IMSId): Servicejob {
    if (id === undefined) {
      return undefined;
    }

    return this.servicejobs$.getValue().find((sj) => {
      if (sj.id.imsId === id.imsId && sj.id.secondaryId === id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  public getServiceJobsForTourId(tourId: IMSId): Servicejob[] {
    if (tourId === undefined) {
      return [];
    }

    return this.servicejobs$.getValue().filter((sj) => {
      if (sj.tour.id.imsId === tourId.imsId) {
        return true;
      }
      return false;
    });
  }

  public getServiceJobsForTourStopId(tourStopId: IMSId): Servicejob[] {
    if (tourStopId === undefined) {
      return [];
    }

    return this.servicejobs$.getValue().filter((sj) => {
      if (sj.tourStop.id.imsId === tourStopId.imsId) {
        return true;
      }
      return false;
    });
  }

  public async updateStatusOfServicejob(id: IMSId, status: ServicejobStatus) {
    let sjobs = this.servicejobs$.getValue();
    for (const job of sjobs) {
      if (job.id.imsId === id.imsId) {
        job.status = status;
      }
    }
    this.servicejobs$.next(sjobs);
    await this.servicejobDaoService.saveServicejobs(sjobs);
  }

  public async updateServicejob(sjob: Servicejob) {
    let sjobs = this.servicejobs$.getValue();
    for (let job of sjobs) {
      if (
        job.id.imsId === sjob.id.imsId ||
        job.id.secondaryId === sjob.id.secondaryId
      ) {
        job = sjob;
      }
    }
    this.servicejobs$.next(sjobs);
    await this.servicejobDaoService.saveServicejobs(sjobs);
  }

  public async updateServicejobs(servicejobs: Servicejob[]) {
    let jobs = this.servicejobs$.getValue();
    for (let job of jobs) {
      let newSjob = servicejobs.find((sjob) => {
        return (
          job.id.imsId === sjob.id.imsId ||
          job.id.secondaryId === sjob.id.secondaryId
        );
      });
      job = newSjob;
    }
    this.servicejobs$.next(jobs);
    await this.servicejobDaoService.saveServicejobs(jobs);
  }

  public async unassignServicejob(sjob: Servicejob) {
    let unassignSjobRab = new UnassignServicejobRemoteActionBuilder(
      sjob,
      this.appService,
      this.credentialService,
      this.logger
    );
    let unassignSjobRA = await unassignSjobRab.generateRemoteAction([]);
    await this.remoteActionService.sendRemoteAction(unassignSjobRA);
    await this.removeServicejob(sjob);
  }

  public async removeServicejob(sjob: Servicejob) {
    await this.servicejobDaoService.removeServicejob(sjob?.id);
    await this.loadLocalServicejobs();
  }

  public async setFinishWithNextServicejobStatusChange(finish: boolean) {
    await this.servicejobDaoService.setFinisWithNextServicejobStatusChange(
      finish
    );
  }
  public async getFinishWithNextServicejobStatusChange(): Promise<boolean> {
    return await this.servicejobDaoService.getFinishWithNextServicejobStatusChange();
  }

  public async setNextTourJobEndNotDelivered(notDelivered: boolean) {
    await this.servicejobDaoService.setNextTourJobEndNotDelivered(notDelivered);
  }
  public async getTourJobStatusEndNotDelivered(): Promise<boolean> {
    return await this.servicejobDaoService.getTourJobStatusEndNotDelivered();
  }

  public async addDeficiencyTicketServicejobs(
    servicejobsDto: ServicejobDTO[]
  ): Promise<void> {
    let servicejobs: Servicejob[] = [];

    for (const servicejobDto of servicejobsDto) {
      let localStatus = await this.localStatusService.loadLocalServicejobStatus(
        {
          imsId: servicejobDto.id,
          secondaryId: undefined,
          objRef: 'ims/ServiceJob',
        }
      );
      servicejobDto.loadedbydeficiencyticket = true;
      servicejobs.push(
        Servicejob.fromDto(
          servicejobDto,
          localStatus,
          undefined,
          this.appParameterService.getFormatLabelForObject('ims/ServiceJob')
        )
      );
    }

    await this.servicejobDaoService.saveServicejobs(servicejobs);
    await this.loadLocalServicejobs();
  }

  // Resetable
  public async reset() {
    await this.servicejobDaoService.reset();
    await this.loadLocalServicejobs();
  }

  public async clear() {
    await this.servicejobDaoService.clear();
    this.servicejobs$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    // await this.servicejobDaoService.clearRemoteServicejobs();
    // JB20200406 - Also need to clear the related services. Otherwise a db update
    // wouldnt work on this services
    // await this.dfService.sync();
    // await this.ejmService.clear();
    // await this.tsService.clear();
    // await this.ciService.clear();
    // await this.oiService.clear();
    await this.loadRemoteServicejobs(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {
    this.temporaryLocalServicejobs = this.servicejobs$
      .getValue()
      .filter((sjob) => {
        return sjob.isLocal();
      });

    await this.dfService.presync();
    await this.tsService.presync();
    await this.ciService.presync();
    await this.oiService.presync();
    await this.rService.presync();
    await this.bService.presync();
    await this.tService.presync();
    await this.boqService.presync();
    await this.jsService.presync();
  }

  public async postsync(): Promise<void> {
    this.logger.log(
      this.constructor.name,
      'Executing postsync -> merge local sjobs with remote jobs by acc number',
      this.temporaryLocalServicejobs.length,
      this.servicejobs$.getValue().length
    );

    // Check if a localservicejob can be found in the new synced sjobs.
    // if yes we can delete the local sjob.
    for (const lsjob of this.temporaryLocalServicejobs) {
      const found = this.servicejobs$.getValue().find((sjob) => {
        return (
          lsjob.appaccountingnumber?.toLocaleLowerCase() === sjob.appaccountingnumber?.toLocaleLowerCase() ||
          lsjob.appaccountingnumber?.toLocaleLowerCase() === sjob.accountingnumber?.toLocaleLowerCase()
        );
      });

      if (found) {
        this.logger.log(
          this.constructor.name,
          'Found a remote sjob for local sjob',
          lsjob.appaccountingnumber,
          'delete the local duplicate'
        );
        await this.servicejobDaoService.deleteLocalServicejob(lsjob.id);
      } else {
        this.logger.log(
          this.constructor.name,
          'No copy of local sjob found',
          lsjob.appaccountingnumber,
          'keep local copy'
        );
      }
    }

    await this.loadLocalServicejobs();

    await this.dfService.postsync();
    await this.tsService.postsync();
    await this.ciService.postsync();
    await this.oiService.postsync();
    await this.rService.postsync();
    await this.bService.postsync();
    await this.tService.postsync();
    await this.boqService.postsync();
    await this.jsService.postsync();
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalServicejobs();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalServicejobs() {
    try {
      this.logger.log(this.constructor.name, '- Load local Servicejobs');
      let servicejobs: Servicejob[] =
        await this.servicejobDaoService.getServicejobs();
      this.logger.log(
        this.constructor.name,
        '- Loaded local Servicejobs',
        servicejobs.length
      );
      this.servicejobs$.next(servicejobs);
      await this.badgesService.clearBadgesFromNotAvailableServiceJobs(
        servicejobs
      );

      this.filterPlannedJobs();
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Servicejobs: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemoteServicejobs(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      let jobsrc =
        this.appParameterService.appParameters$.getValue()?.[
          'imsmc.mode.jobsource'
        ];
      this.logger.log(
        this.constructor.name,
        '- Load new Servicejobs from IMS for jobsrc',
        jobsrc,
        'on endpoint',
        getEndpointForJobSource(jobsrc)
      );

      const additionalData = {
        synckey: syncKey
      };
      if (
        jobsrc == JobSource.syncTeamPlannedJobsWithBranches ||
        jobsrc == JobSource.syncNoTourJobsWithBranches
      ) {
        const currentSjobId = this.csService.theState?.getValue()?.serviceJobId
        const additionalJobsIds: Set<IMSId> = new Set();
        (await this.preferencesService.getFavouriteServicejobs() ?? []).forEach(it => additionalJobsIds.add(it))
        if (currentSjobId) {
          additionalJobsIds.add(currentSjobId)
        }
        // SD-11041: do not request all servicejobs again, otherwise sjobs in projectplaning are staying in the app forever
        // (this.servicejobs$.getValue() ?? []).forEach(it => additionalJobsIds.add(it?.id))

        additionalData['requestingAdditionalSjobs'] = Array.from(additionalJobsIds.values())
          .map((id) => id.imsId)
          .join(',');
      }

      try {
        const data: any = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: getEndpointForJobSource(jobsrc),
            data: additionalData,
            cachable: false,
            fifo: false,
            authenticationRequired: true,
          })
        );
        this.logger.log(
          this.constructor.name,
          '- JB2021 Loaded new Servicejobs from IMS for jobsrc',
          jobsrc,
        );
        await this.parseData(data, apiLevel);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Servicejobs: ',
          error?.message
        );
        throw error;
      }
    }
  }

  private filterPlannedJobs() {
    this.logger.log(this.constructor.name, 'Filter planned jobs');
    this.ejmService.empJobMappings$.subscribe({
      next: async (mappings) => {
        let plannedServiceJobs: Servicejob[] = [];
        if (mappings) {
          for (const sjob of this.servicejobs$.getValue()) {
            await this.createMappingsForTodaysLocalServicejobIfNecessary(sjob);
          }
          for (const mapping of mappings) {
            if (TimeUtils.isToday(mapping.plannedfromts)) {
              const job = this.getServiceJobById(mapping.jobid);
              plannedServiceJobs.push(job);
            }
          }
        }
        this.logger.log(
          this.constructor.name,
          'Filtered',
          plannedServiceJobs.length,
          ' planned jobs'
        );
        this.plannedServicejobs$.next(plannedServiceJobs);
      },
    });
  }

  private async createMappingsForTodaysLocalServicejobIfNecessary(
    serviceJob: Servicejob
  ) {
    if (
      serviceJob &&
      serviceJob.isLocal() &&
      TimeUtils.isToday(serviceJob.createdtstamp)
    ) {
      this.logger.log(
        this.constructor.name,
        'Found a local servicejob that was created today',
        serviceJob
      );
      for (const mapping of this.ejmService.empJobMappings$.getValue()) {
        if (
          TimeUtils.isToday(mapping.plannedfromts) &&
          IMSObject.equals(mapping.jobid, serviceJob.id)
        ) {
          this.logger.log(
            this.constructor.name,
            serviceJob.label,
            'has already a mapping',
            mapping
          );
          return;
        }
      }
      this.logger.log(
        this.constructor.name,
        serviceJob.label,
        'does not have a mapping for today, create one'
      );
      this.ejmService.createEmpJobMappingForToday(serviceJob);
    }
  }

  private async parseData(data: any, apiLevel: APILevel) {
    if (
      this.appParameterService.appParameters$.getValue()?.[
        'imsmc.mode.jobsource'
      ] === JobSource.syncNoTourJobs
    ) {
      // Only the Jobs array will be received
      await this.servicejobDaoService.clearRemoteServicejobs();
      await this.parseServicejobsDTOArray(data);
    } else {
      // Parse each component of the received
      await this.dfService.clearOnlyDefinitions();
      await this.ejmService.clear();
      await this.tsService.clear();
      await this.ciService.clear();
      await this.oiService.clear();
      await this.rService.clear();
      // Do not clear branches because branches also has a own sync which already loaded data
      // await this.bService.clear()
      await this.tService.clear();
      await this.boqService.clear();
      await this.boqpService.clear();
      if (apiLevel < APILevel.DEDICATED_JOBSECTIONS) {
        await this.jsService.clear();
      }
      await this.ttService.clear();
      await this.tteService.clear();
      await this.servicejobDaoService.clearRemoteServicejobs();

      if (data.cargoitems) {
        this.logger.warn(
          this.constructor.name,
          '- array for cargoitems found',
          data.cargoitems?.length
        );
        await this.ciService.updateCargoItems(data.cargoitems);
      }
      if (data.orderitems) {
        this.logger.warn(
          this.constructor.name,
          '- array for orderitems found',
          data.orderitems?.length
        );
        await this.oiService.updateOrderItems(data.orderitems);
      }
      if (data.reasons) {
        this.logger.warn(
          this.constructor.name,
          '- array for reasons found',
          data.reasons
        );
        await this.rService.updateReasons(data.reasons);
      }
      if (data.dynforms) {
        await this.dfService?.updateDynForms(data.dynforms);
      }
      if (data.branches) {
        this.logger.warn(
          this.constructor.name,
          '- array for branches found',
          data.branches?.length
        );
        await this.bService.updateBranches(data.branches);
      }
      if (data.tours) {
        this.logger.warn(
          this.constructor.name,
          '- array for tours found',
          data.tours?.length
        );
        await this.tService.updateTours(data.tours);
      }
      if (data.tourStops) {
        this.logger.warn(
          this.constructor.name,
          '- array for tourStops found',
          data.tourStops?.length
        );
        await this.tsService.updateTourStops(data.tourStops);
      }
      if (data.employees) {
        await this.employeeService.updateAny(data.employees);
      }
      if (data.empjobmapping) {
        await this.ejmService.updateEmpJobMapping(data.empjobmapping);
        this.filterPlannedJobs();
      }
      if (data.billofquantities) {
        await this.boqService?.updateBillOfQuantities(data.billofquantities);
      }
      if (data.billofquantitiespositions) {
        await this.boqpService?.updateBillOfQuantitiesPositions(
          data.billofquantitiespositions
        );
      }
      if (data.jobsections && apiLevel < APILevel.DEDICATED_JOBSECTIONS) {
        await this.jsService?.updateJobSections(data.jobsections);
      }
      if (data.tagtemplates) {
        await this.ttService?.updateTagTemplates(data.tagtemplates);
      }
      if (data.tagtemplateselements) {
        await this.tteService?.updateTagTemplatesElements(
          data.tagtemplateselements
        );
      }
      if (data.jobs) {
        await this.parseServicejobsDTOArray(data.jobs);
      }
    }
  }

  private async parseServicejobsDTOArray(servicejobsDto: ServicejobDTO[]) {
    let servicejobs: Servicejob[] = [];

    for (const servicejobDto of servicejobsDto) {
      let localStatus = await this.localStatusService.loadLocalServicejobStatus(
        {
          imsId: servicejobDto.id,
          secondaryId: undefined,
          objRef: 'ims/ServiceJob',
        }
      );
      servicejobs.push(
        Servicejob.fromDto(
          servicejobDto,
          localStatus,
          undefined,
          this.appParameterService.getFormatLabelForObject('ims/ServiceJob')
        )
      );
    }

    console.log("JBKRK synced submitteddynformsyscodeststamps", servicejobs.map(it => it.submittedDynFormSyscodesTstamps))

    this.logger.log(
      this.constructor.name,
      'Downloaded',
      servicejobs.length,
      'Servicejobs'
    );

    try {
      await this.servicejobDaoService.saveServicejobs(servicejobs);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Could not save service jobs',
        error?.message
      );
    }

    this.servicejobs$.next(servicejobs);
  }

  async initializeCompleted(): Promise<void> {}
}
